
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DividerBlockConfiguration } from "@/types/block-configurations";
import { PaddingControls, ResponsiveControls, BorderControls, AlignmentControls } from "../shared";

interface DividerBlockPropertiesProps {
  config: DividerBlockConfiguration;
  onChange: (config: DividerBlockConfiguration) => void;
}

export function DividerBlockProperties({ config, onChange }: DividerBlockPropertiesProps) {
  const updateField = <K extends keyof DividerBlockConfiguration>(
    field: K,
    value: DividerBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Quick Presets */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Quick Presets</Label>
        <div className="grid grid-cols-3 gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              updateField('border', { ...config.border, width: 1, style: 'solid' });
            }}
          >
            Thin
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              updateField('border', { ...config.border, width: 2, style: 'solid' });
            }}
          >
            Medium
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              updateField('border', { ...config.border, width: 4, style: 'solid' });
            }}
          >
            Thick
          </Button>
        </div>
      </div>

      {/* Divider Width */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Divider Width</Label>
        <Input
          type="text"
          value={config.width}
          onChange={(e) => updateField('width', e.target.value)}
          placeholder="100% or 600px"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Use 100% to span the full width, or enter a fixed pixel value like 300px
        </p>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["border"]} className="w-full">
        
        <AccordionItem value="border">
          <AccordionTrigger>Line Style</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
